import { useEffect, useState } from 'react'
import { useFileBrowserStore } from '../stores/fileBrowserStore'
import { fileName } from '@/shared/api/generated'
import type { S3Object } from '@/shared/api/generated'

function sortedChildren(items: S3Object[]): S3Object[] {
  return [...items].sort((a, b) => fileName(a).localeCompare(fileName(b), undefined, { sensitivity: 'base' }))
}

interface FolderTreeProps {
  onDrop?: (targetObj: S3Object) => void
}

export function FolderTree({ onDrop }: FolderTreeProps) {
  const { treeChildren, treeLoading, toggleExpand } = useFileBrowserStore()

  useEffect(() => {
    if (!treeChildren['/']) {
      toggleExpand('/', { key: '/' } as S3Object)
    }
  }, [])

  const roots = sortedChildren(treeChildren['/'] || [])
  const loading = treeLoading.has('/')

  return (
    <div className="folder-tree" data-testid="folder-tree">
      <div className="tree-header">Folders</div>
      {loading && <div className="muted">Loading…</div>}
      {!loading && roots.length === 0 && <div className="muted">No groups</div>}
      {roots.map(obj => (
        <TreeNode key={obj.key} obj={obj} depth={0} onDrop={onDrop} />
      ))}
    </div>
  )
}

function TreeNode({ obj, depth, onDrop }: { obj: S3Object; depth: number; onDrop?: (targetObj: S3Object) => void }) {
  const { expandedFolders, treeChildren, treeHasMore, treeLoading, currentPath, navigateTo, toggleExpand, loadMoreTree, rootGroups } = useFileBrowserStore()
  const [dropTarget, setDropTarget] = useState(false)
  const expanded = expandedFolders.has(obj.key)
  const isLoading = treeLoading.has(obj.key)
  const children = sortedChildren(treeChildren[obj.key] || [])
  const isActive = currentPath === obj.key
  const isGroup = depth === 0
  const name = fileName(obj)
  const g = isGroup ? rootGroups.get(obj.key) : undefined
  const roleIcon = isGroup
    ? (g?.view_id
      ? (g.base_path?.startsWith('//') ? '🗄️' : '🔗')
      : (g?.role) === 'admin' ? '👑' : '📁')
    : '📁'

  const handleClick = () => {
    if (isGroup) {
      if (g) {
        navigateTo(obj.key, g.id, g.name, g.role, g.perms, g.view_id ?? 0, g.registry_enabled)
      } else {
        navigateTo(obj.key)
      }
    } else {
      navigateTo(obj.key)
    }
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    toggleExpand(obj.key, obj)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDropTarget(true)
  }
  const handleDragLeave = () => setDropTarget(false)
  const handleDropOnNode = (e: React.DragEvent) => {
    e.preventDefault()
    setDropTarget(false)
    onDrop?.(obj)
  }

  const cls = ['tree-node']
  if (isActive) cls.push('active')
  if (dropTarget) cls.push('drop-target')

  return (
    <div>
      <div
        className={cls.join(' ')}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDropOnNode}
        data-testid="tree-node"
      >
        <span className="toggle" onClick={handleToggle}>
          {isLoading ? '…' : expanded ? '▼' : '▶'}
        </span>
        <span className="icon">{roleIcon}</span>
        <span className="label">{name}</span>
      </div>
      {expanded && (
        <>
          {children.map(child => (
            <TreeNode key={child.key} obj={child} depth={depth + 1} onDrop={onDrop} />
          ))}
          {treeHasMore[obj.key] && (
            <button
              className="load-more"
              style={{ paddingLeft: `${(depth + 1) * 16 + 8}px` }}
              onClick={(e) => { e.stopPropagation(); loadMoreTree(obj.key, obj) }}
            >
              Load more…
            </button>
          )}
        </>
      )}
    </div>
  )
}
